import { api } from "./api.js";

/**
 * Business card orders (Sales > Business cards): what the card studio on the platform's
 * own sites sold, with the buyer, the design, the invoice and the print file. The orders
 * live in the platform database, not the CRM's; the invoice is the CRM's own
 * (`apps/api/src/modules/card-orders/routes/crm.ts`).
 */

export interface CardOrderBilling {
  kind: "person" | "company";
  name: string;
  regCode: string;
  vatNumber: string;
  address: string;
}

export interface CardOrderRow {
  id: string;
  code: string;
  /** pending | paid | delivered (the file is kept) */
  status: string;
  email: string;
  locale: string;
  names: number;
  design: string;
  amountCents: number;
  currency: string;
  /** Null on an order made before the studio asked who the invoice is for. */
  billing: CardOrderBilling | null;
  invoiceId: string;
  invoiceNumber: string;
  /** Made with test keys: its payment page was a test one (a paid one's invoice is numbered outside the sequence). */
  test: boolean;
  /** The desk may delete it: a test order, or an unpaid one that can no longer be paid. Never a sale. */
  removable: boolean;
  hasFile: boolean;
  fileName: string;
  fileBytes: number;
  createdAt: string;
  paidAt: string | null;
}

export interface CardOrderDetail extends CardOrderRow {
  cards: { company: string; people: Array<{ name: string; role: string }> };
  pageUrl: string;
  clientId: string | null;
}

export interface CardOrdersPage {
  rows: CardOrderRow[];
  total: number;
  page: number;
  pageSize: number;
}

export type CardOrderFilter = "all" | "paid" | "pending";

export interface CardOrdersQuery {
  q?: string;
  status?: CardOrderFilter;
  page?: number;
  pageSize?: number;
}

function qs(params: CardOrdersQuery): string {
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

export const getCardOrders = (params: CardOrdersQuery) => api.get<CardOrdersPage>(`/admin/crm/card-orders${qs(params)}`);
export const getCardOrder = (id: string) => api.get<CardOrderDetail>(`/admin/crm/card-orders/${encodeURIComponent(id)}`);

/** Save the kept print file (a fetch with the session cookie and a blob, like the PDFs). */
export async function downloadCardOrderFile(id: string, fileName: string): Promise<void> {
  const res = await fetch(`/api/admin/crm/card-orders/${encodeURIComponent(id)}/file`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** What "make the print file again" answers. */
export interface CardOrderRemake {
  ok: true;
  /** False when the order kept no file: nothing was changed. */
  dropped: boolean;
  /** The order let go of its file, but storage did not remove the old one. */
  leftInStorage: boolean;
  /** The order as it reads now. */
  order: CardOrderRow;
}

/**
 * The first half of "make the print file again": the kept file is deleted. The server
 * cannot draw a print file (a browser does), so the second half is `cardOrderPageLink`:
 * the studio page, opened for this order, draws a new file and hands it in. No letter is
 * sent from here.
 */
export const remakeCardOrderFile = (id: string) => api.del<CardOrderRemake>(`/admin/crm/card-orders/${encodeURIComponent(id)}/file`);

/**
 * A link to the studio page with the order in it, for a paid order that keeps no file. The
 * page puts the order's own cards back, draws the print file and hands it in. The link
 * holds a key that opens the order for an hour: open it, do not keep or show it.
 */
export const cardOrderPageLink = (id: string) => api.post<{ ok: true; url: string }>(`/admin/crm/card-orders/${encodeURIComponent(id)}/page-link`);

/** Why an order of a delete stayed (`apps/api/src/modules/studio-orders/remove/`). */
export type OrderRemoveRefusal = "sale" | "open" | "not_found" | "changed";

/** What a delete of ticked orders answers, for card orders and logo orders alike. */
export interface OrdersRemoved {
  removed: Array<{
    id: string;
    code: string;
    /** Its test invoice left the CRM, and the client made for the test with it. */
    invoiceRemoved: boolean;
    clientRemoved: boolean;
    /** The CRM did not answer: the test invoice is still there, to delete by hand. */
    invoiceLeft: boolean;
    /** Storage kept a file. The server's log names it; nothing for the desk to do. */
    leftInStorage: boolean;
  }>;
  refused: Array<{ id: string; code: string; reason: OrderRemoveRefusal }>;
}

/**
 * Delete the ticked orders. The server takes only what may go (a test order, an unpaid one
 * that can no longer be paid), with its files and its test invoice, and says why it kept
 * the rest. No letter is sent.
 */
export const removeCardOrders = (ids: string[]) => api.post<OrdersRemoved>("/admin/crm/card-orders/bulk-delete", { ids });

/** Paid means the money came, whether the file is kept yet or not. */
export function isPaidOrder(row: Pick<CardOrderRow, "status">): boolean {
  return row.status === "paid" || row.status === "delivered";
}

/** A file can be made again only where one is kept, and only for an order whose money came. */
export function canRemakeFile(row: Pick<CardOrderRow, "status" | "hasFile">): boolean {
  return isPaidOrder(row) && row.hasFile;
}

/** A paid order that keeps no file: the studio page can draw one for it. */
export function canMakeFile(row: Pick<CardOrderRow, "status" | "hasFile">): boolean {
  return isPaidOrder(row) && !row.hasFile;
}

/** Who ordered, in one line: the invoice's name, or the address when there is none. */
export function orderBuyer(row: Pick<CardOrderRow, "billing" | "email">): string {
  return row.billing?.name || row.email;
}

/** The name a saved file gets: the kept file's own name, or the order's code. */
export function orderFileName(row: Pick<CardOrderRow, "fileName" | "code">): string {
  return row.fileName || `${row.code}.pdf`;
}
